/**
 * Pakete-Widget (docs/KONZEPT-PAKETE-WIDGET.md § 2, § 4) — die Regeln, ab wann ein Katalog-Paket
 * auf der Website stehen darf, und wie sein Preistext lautet.
 *
 * WARUM HIER: Die API entscheidet (server-autoritativ), die Pflege-UI zeigt dieselbe Regel vorab
 * (Schalter gesperrt, Vorschau in vier Sprachen, 30-%-Rueckfrage). `apps/web` kann nicht aus
 * `apps/api` importieren — zwei Kopien liefen auseinander.
 *
 * PREIS-INTEGRITAET: hier wird nichts gerechnet ausser einem Verhaeltnis fuer die Tippfehler-
 * Rueckfrage. Der Betrag kommt aus der Datenbank (`paketPreisart`), formatiert mit `euroKurz`.
 */
import { paketPreisart, type PaketPreisQuelle } from "./paket-preisart";
import { euroKurz } from "./spanne";
import type { AnzeigeSprache } from "./format";

/** Sprachen neben Deutsch, die die Website zeigt. Deutsch steht in name/description. */
export const WEBSITE_UEBERSETZUNGS_SPRACHEN = ["en", "it", "fr"] as const;
export type WebsiteUebersetzungsSprache = (typeof WEBSITE_UEBERSETZUNGS_SPRACHEN)[number];
export type PaketUebersetzungen = Partial<Record<WebsiteUebersetzungsSprache, { name?: string | null; description?: string | null; art?: string | null }>>;

const gefuellt = (s: string | null | undefined): boolean => typeof s === "string" && s.trim().length > 0;

/**
 * Die Sprachen, die fuer die Website noch fehlen. Name ist immer Pflicht; die Beschreibung nur,
 * wenn es eine deutsche gibt (ohne deutschen Text gibt es nichts zu uebersetzen).
 */
export function fehlendeWebsiteSprachen(
  deutsch: { description?: string | null },
  uebersetzungen: PaketUebersetzungen | null | undefined,
): WebsiteUebersetzungsSprache[] {
  const mitBeschreibung = gefuellt(deutsch.description);
  return WEBSITE_UEBERSETZUNGS_SPRACHEN.filter((l) => {
    const t = uebersetzungen?.[l];
    return !gefuellt(t?.name) || (mitBeschreibung && !gefuellt(t?.description));
  });
}

/** Ein Preis gehoert nie in eine Uebersetzung — den Preistext setzt MAESTRO. */
const PREIS_IM_TEXT = /\d[\d.,]*\s*(€|eur\b|euro)|(€|\beur\b|\beuro)\s*\d/i;
export function enthaeltPreisangabe(text: string | null | undefined): boolean {
  return typeof text === "string" && PREIS_IM_TEXT.test(text);
}

/** Ab dieser relativen Aenderung fragt MAESTRO bei einem sichtbaren Paket nach (Tippfehler-Schutz). */
export const PREIS_SPRUNG_SCHWELLE = 0.3;

/**
 * Relative Preisaenderung (0.5 = 50 %), oder `null`, wenn es nichts zu vergleichen gibt (alter
 * Preis 0 = noch kein Preis, oder unveraendert). Nur ein Verhaeltnis fuer die Rueckfrage, kein Betrag.
 */
export function preisSprung(altCents: number, neuCents: number): number | null {
  if (!(altCents > 0) || altCents === neuCents) return null;
  return Math.abs(neuCents - altCents) / altCents;
}
export function istPreisSprung(altCents: number, neuCents: number): boolean {
  const s = preisSprung(altCents, neuCents);
  return s !== null && s > PREIS_SPRUNG_SCHWELLE;
}

const PRO_PERSON: Record<AnzeigeSprache, (betrag: string) => string> = {
  de: (b) => `${b} pro Person, inkl. MwSt.`,
  en: (b) => `${b} per person, incl. VAT`,
  it: (b) => `${b} a persona, IVA inclusa`,
  fr: (b) => `${b} par personne, TVA incluse`,
};
const PAUSCHAL: Record<AnzeigeSprache, (betrag: string) => string> = {
  de: (b) => `${b} pauschal, inkl. MwSt.`,
  en: (b) => `${b} flat rate, incl. VAT`,
  it: (b) => `${b} forfettario, IVA inclusa`,
  fr: (b) => `${b} forfait, TVA incluse`,
};
const AB_GAESTE: Record<AnzeigeSprache, (n: number) => string> = {
  de: (n) => `ab ${n} Personen`,
  en: (n) => `from ${n} guests`,
  it: (n) => `da ${n} persone`,
  fr: (n) => `à partir de ${n} personnes`,
};

const BIS_GAESTE: Record<AnzeigeSprache, (n: number) => string> = {
  de: (n) => `bis ${n} Personen`,
  en: (n) => `up to ${n} guests`,
  it: (n) => `fino a ${n} persone`,
  fr: (n) => `jusqu'à ${n} personnes`,
};

/** „bis 40 Personen“ — nur die Hoechst-Gaestezahl als Text (eine Gaestezahl, kein Preis); `null` ohne gueltige Zahl. */
export function paketWebsiteMaxGaesteText(maxGuests: number | null | undefined, sprache: AnzeigeSprache): string | null {
  return typeof maxGuests === "number" && Number.isInteger(maxGuests) && maxGuests > 0 ? BIS_GAESTE[sprache](maxGuests) : null;
}

/** „35 € pro Person, inkl. MwSt.“ — fuer Menues mit Preis je Gast (Reisegruppen-Widget). */
export function proPersonPreisText(betragCents: number, sprache: AnzeigeSprache): string {
  return PRO_PERSON[sprache](euroKurz(betragCents, sprache));
}

/**
 * Preistext eines einzelnen Website-Pakets, auch fuer Pauschalpakete („1.200 € pauschal, inkl.
 * MwSt.“). Nie „ab“ beim Preis; `null` ohne Betrag (ein Paket ohne Preis erscheint nicht).
 * `gaeste` nennt nur die Mindest-Gaestezahl — das ist eine Gaestezahl, kein Preis.
 */
export function paketWebsitePreisText(
  paket: PaketPreisQuelle & { minGuests: number | null },
  sprache: AnzeigeSprache,
): { preis: string | null; gaeste: string | null } {
  const art = paketPreisart(paket);
  const ok = art.betragCents > 0 && Number.isInteger(art.betragCents);
  const betrag = euroKurz(art.betragCents, sprache);
  return {
    preis: ok ? (art.pauschal ? PAUSCHAL : PRO_PERSON)[sprache](betrag) : null,
    gaeste: paket.minGuests != null && paket.minGuests > 0 ? AB_GAESTE[sprache](paket.minGuests) : null,
  };
}

const AB: Record<AnzeigeSprache, string> = { de: "ab", en: "from", it: "da", fr: "à partir de" };

/**
 * Kategorie-Sammelangabe („ab 45 € pro Person, inkl. MwSt.“, Konzept § 8 Antwort 1): der
 * guenstigste Preis der sichtbaren Pakete, von MAESTRO selbst eingesetzt. Pro-Person- und
 * Pauschalpreise sind nicht vergleichbar: es zaehlen die Pro-Person-Pakete, nur ohne solche die
 * Pauschalen. `null`, wenn kein Paket einen Preis hat.
 */
export function kategorieAbPreis(
  pakete: PaketPreisQuelle[],
  sprache: AnzeigeSprache,
): { cents: number; pauschal: boolean; text: string } | null {
  const arten = pakete.map(paketPreisart).filter((a) => a.betragCents > 0 && Number.isInteger(a.betragCents));
  const kandidaten = arten.some((a) => !a.pauschal) ? arten.filter((a) => !a.pauschal) : arten;
  if (kandidaten.length === 0) return null;
  const min = kandidaten.reduce((a, b) => (b.betragCents < a.betragCents ? b : a));
  const text = (min.pauschal ? PAUSCHAL : PRO_PERSON)[sprache](euroKurz(min.betragCents, sprache));
  return { cents: min.betragCents, pauschal: min.pauschal, text: `${AB[sprache]} ${text}` };
}

/**
 * Speisekarten-Widget P4 (docs/LOOP-SPEISEKARTEN-WIDGET.md): Preistext einer Position („12,50 €“,
 * „14 €“, en „€14“). `null` ohne gueltigen Betrag oder bei 0 (der Import setzt Positionen ohne
 * Preis auf 0 Cent; die Website zeigt dort keinen Preis — also kein Preis, keine Punktlinie).
 */
export function speisekartePreisText(cents: unknown, sprache: AnzeigeSprache): string | null {
  return typeof cents === "number" && Number.isInteger(cents) && cents > 0 ? euroKurz(cents, sprache) : null;
}

/**
 * Speisekarten-Widget P4 / Punkt 147: „ab 12 €“ einer Karte oder Kategorie — der guenstigste
 * Positionspreis > 0. `null`, wenn keine Position einen Preis hat.
 */
export function speisekarteAbPreis(cents: readonly unknown[], sprache: AnzeigeSprache): { cents: number; text: string } | null {
  const gueltig = cents.filter((c): c is number => typeof c === "number" && Number.isInteger(c) && c > 0);
  if (gueltig.length === 0) return null;
  const min = Math.min(...gueltig);
  return { cents: min, text: `${AB[sprache]} ${euroKurz(min, sprache)}` };
}
